import type { ConnectionTierTransition } from './connection-tier-live-kit.hook'
import type { ConnectionQualityMetrics } from '../api/report-connection-quality.hook'

import { toAnalyticsId } from '@shared/lib/amplitude'

import { ConnectionTier } from './types/classroom/enums'
import { useLessonRoomStore } from './store'
import { useReportConnectionQualityApi } from '../api/report-connection-quality.hook'
import { detectBrowser, detectOs } from '../lib/user-agent'
import { trackClassroomEvent } from '../lib/classroom'

interface ConnectionReportLiveKitReturn {
  report: (transition: ConnectionTierTransition) => void
}

const DEGRADED_TIERS: ConnectionTier[] = [ConnectionTier.Weak, ConnectionTier.Lost]
const MS_PER_SECOND = 1000
const PERCENT = 100
const PERCENT_DECIMALS = 10

const isAmplitudeWorthy = (transition: ConnectionTierTransition): boolean =>
  DEGRADED_TIERS.includes(transition.tier) || DEGRADED_TIERS.includes(transition.previousTier) || false

const toMilliseconds = (seconds: number | null): number | null =>
  (seconds === null ? null : Math.round(seconds * MS_PER_SECOND))

const toPercent = (ratio: number | null): number | null =>
  (ratio === null ? null : Math.round(ratio * PERCENT * PERCENT_DECIMALS) / PERCENT_DECIMALS)

const buildMetrics = (transition: ConnectionTierTransition): ConnectionQualityMetrics => {
  const { own, peer, peerLivekitQuality } = transition.evidence

  return {
    loss_pct: transition.reading.lossPct,
    rtt_ms: transition.reading.rttMs,
    jitter_ms: transition.reading.jitterMs,
    limitation_reason: transition.limitationReason,
    livekit_quality: transition.livekitQuality,
    peer_livekit_quality: peerLivekitQuality,
    video_frozen_ms: toMilliseconds(own.frozenSeconds),
    audio_concealment_pct: toPercent(own.concealmentRatio),
    own_reception_degraded: own.isDegraded,
    peer_video_frozen_ms: toMilliseconds(peer?.frozenSeconds ?? null),
    peer_audio_concealment_pct: toPercent(peer?.concealmentRatio ?? null),
    peer_reception_degraded: peer?.isDegraded ?? null,
    uplink_impaired: own.isUplinkImpaired,
    peer_uplink_impaired: peer?.isUplinkImpaired ?? null,
    cpu_limited: own.isCpuLimited,
    peer_cpu_limited: peer?.isCpuLimited ?? null,
    browser: detectBrowser(navigator.userAgent),
    os: detectOs(navigator.userAgent),
  }
}

export const useConnectionReportLiveKit = (): ConnectionReportLiveKitReturn => {
  const lessonRoomStore = useLessonRoomStore()
  const { reportConnectionQuality } = useReportConnectionQualityApi()

  const report = (transition: ConnectionTierTransition): void => {
    if (!transition.isSelf) return

    const bookingId = lessonRoomStore.getBooking?.id
    if (!bookingId) return

    void reportConnectionQuality(bookingId, transition.tier, buildMetrics(transition))

    if (!isAmplitudeWorthy(transition)) return

    trackClassroomEvent('Connection_change', {
      name: 'connection_quality',
      connection_quality: toAnalyticsId(transition.tier),
      packet_loss_pct: transition.reading.lossPct,
      rtt_ms: transition.reading.rttMs,
      jitter_ms: transition.reading.jitterMs,
      reason: transition.limitationReason ? toAnalyticsId(transition.limitationReason) : null,
      is_self: transition.isSelf,
      user_role: 'customer',
    })
  }

  return { report }
}
